import { useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { WEBSOCKET_URL } from '../config';
import { useDeploymentJobStatus } from './useDeploymentJobStatus';

export interface ExecutionProgress {
  current: number;
  total: number;
  lastIssueKey?: string;
  lastError?: string;
}

export interface ExecutionLogEntry {
  type: 'created' | 'failed' | 'skipped';
  itemId: number;
  issueKey?: string;
  error?: string;
}

export interface ExecutionSummary {
  created: number;
  failed: number;
  skipped: number;
  total: number;
}

interface UseDeploymentExecutionOptions {
  onCompleted?: (summary: ExecutionSummary) => void;
  notify?: boolean;
  deploymentName?: string;
}

export const useDeploymentExecution = (
  deploymentId: string | undefined,
  jobId: string | null,
  initialTotal: number,
  options: UseDeploymentExecutionOptions = {}
) => {
  const { onCompleted, notify = true, deploymentName } = options;
  const completedHandledRef = useRef(false);
  const [progress, setProgress] = useState<ExecutionProgress>({ current: 0, total: initialTotal });
  const [logs, setLogs] = useState<ExecutionLogEntry[]>([]);
  const [completed, setCompleted] = useState(false);
  const [summary, setSummary] = useState<ExecutionSummary | null>(null);
  const [socketConnected, setSocketConnected] = useState(false);

  useEffect(() => {
    completedHandledRef.current = false;
    setProgress({ current: 0, total: initialTotal });
    setLogs([]);
    setCompleted(false);
    setSummary(null);
  }, [jobId, initialTotal]);

  const { status: jobStatus } = useDeploymentJobStatus(deploymentId ?? '', jobId, {
    enabled: !!jobId && !!deploymentId && !completed,
    refetchInterval: !!jobId && !!deploymentId && !completed ? 1000 : false,
  });

  const handleCompletion = (s: ExecutionSummary) => {
    if (completedHandledRef.current) return;
    completedHandledRef.current = true;
    setSummary(s);
    setCompleted(true);

    if (notify && typeof window !== 'undefined' && 'Notification' in window) {
      const fire = () => {
        if (Notification.permission !== 'granted') return;
        const title = s.failed > 0 ? 'Déploiement terminé avec erreurs' : 'Déploiement terminé';
        const body = `${deploymentName ? `${deploymentName} — ` : ''}${s.created} créés · ${s.failed} échecs · ${s.skipped} ignorés · ${s.total} total`;
        try {
          new Notification(title, { body, tag: `deployment-${deploymentId}` });
        } catch {
          /* noop */
        }
      };
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(fire).catch(() => {});
      } else {
        fire();
      }
    }

    onCompleted?.(s);
  };

  useEffect(() => {
    if (!jobId || !deploymentId) return;
    if (completed || !jobStatus) return;

    if (jobStatus.progress && typeof jobStatus.progress === 'object') {
      const p = jobStatus.progress as any;
      setProgress((prev) => ({
        current: Math.max(prev.current, p.current ?? 0),
        total: p.total ?? prev.total ?? initialTotal,
      }));
    }
    if (jobStatus.state === 'completed') {
      const ret = (jobStatus.returnvalue ?? {}) as any;
      handleCompletion({
        created: ret.created ?? 0,
        failed: ret.failed ?? 0,
        skipped: ret.skipped ?? 0,
        total: ret.total ?? initialTotal,
      });
    } else if (jobStatus.state === 'failed') {
      handleCompletion({ created: 0, failed: initialTotal, skipped: 0, total: initialTotal });
    }
  }, [jobStatus, jobId, deploymentId, completed, initialTotal]);

  useEffect(() => {
    if (!jobId || !deploymentId) return;

    const authToken = localStorage.getItem('userAuthToken');
    if (!authToken) return;

    const socket: Socket = io(WEBSOCKET_URL, {
      transports: ['websocket', 'polling'],
      auth: { token: authToken },
    });

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('join', `deployment:${deploymentId}`);
    });
    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('connect_error', () => setSocketConnected(false));

    socket.on('deployment:item:created', (data: any) => {
      if (data.deploymentId !== deploymentId) return;
      setProgress((prev) => ({
        current: Math.max(prev.current, data.current),
        total: data.total ?? prev.total,
        lastIssueKey: data.issueKey,
      }));
      setLogs((prev) => [...prev, { type: 'created', itemId: data.itemId, issueKey: data.issueKey }]);
    });

    socket.on('deployment:item:failed', (data: any) => {
      if (data.deploymentId !== deploymentId) return;
      setProgress((prev) => ({
        current: Math.max(prev.current, data.current),
        total: data.total ?? prev.total,
        lastError: data.error,
      }));
      setLogs((prev) => [...prev, { type: 'failed', itemId: data.itemId, error: data.error }]);
    });

    socket.on('deployment:item:skipped', (data: any) => {
      if (data.deploymentId !== deploymentId) return;
      setProgress((prev) => ({
        current: Math.max(prev.current, data.current),
        total: data.total ?? prev.total,
        lastIssueKey: data.issueKey,
      }));
      setLogs((prev) => [...prev, { type: 'skipped', itemId: data.itemId, issueKey: data.issueKey }]);
    });

    socket.on('deployment:completed', (data: any) => {
      if (data.deploymentId !== deploymentId) return;
      handleCompletion({
        created: data.created,
        failed: data.failed,
        skipped: data.skipped ?? 0,
        total: data.total,
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [deploymentId, jobId]);

  return { progress, logs, completed, summary, socketConnected };
};
